import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AccessToken, OktaAuth } from '@okta/okta-auth-js';
import { ENV_CONFIG } from '@sc/data-access-environment';
import {
  DossierToken,
  Environment,
  Fitter,
  FitterAccessType,
  OpenArrivalToken,
  PreDeclarationToken,
  CtaAction,
} from '@sc/data-access-models';
import { JsonListKey, StorageService } from '@sc/data-access-storage';
import { DialogService } from '@sc/feature-dialog';
import { BehaviorSubject } from 'rxjs';
import { AuthOktaConfig } from '../../types';
import { AUTH_OKTA_MODULE_CONFIG } from '../auth-okta-module-config';

@Injectable({
  providedIn: 'root',
})
export class AuthOktaService {
  // Create a local property for login status
  private isAuthenticated: boolean = null;
  ENCRYPTED_INFO_KEY = 'encrypted-info';
  storagePlace: Storage;

  private readonly _authSubject = new BehaviorSubject<boolean>(false);
  readonly _dossierNumber = new BehaviorSubject<string>(null);
  readonly authSubject$ = this._authSubject.asObservable();
  connectedUser = new EventEmitter();
  isShowErrorDialogOpened = false;

  /**
   * IMPORTANT : We should not inject anything that is dependent on the config or config service in this constructor
   */
  constructor(
    @Inject(OKTA_AUTH) protected oktaAuth: OktaAuth,
    protected oktaAuthStateService: OktaAuthStateService,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly dialogService: DialogService,
    @Inject(AUTH_OKTA_MODULE_CONFIG) protected authOktaConfig: AuthOktaConfig,
    @Inject(ENV_CONFIG) protected env: Environment
  ) {
    this.storagePlace =
      env?.auth?.tokenManager?.storage === 'sessionStorage'
        ? sessionStorage
        : localStorage;

    this.oktaAuthStateService.authState$.subscribe((authState) => {
      const oldState = this.isAuthenticated;
      this.isAuthenticated = authState.isAuthenticated;
      if (!oldState && this.isAuthenticated) {
        this._authSubject.next(true);
      } else if (oldState && !this.isAuthenticated) {
        this._authSubject.next(false);
      }
    });

    setTimeout(async () => {
      this.isAuthenticated = await this.oktaAuth.isAuthenticated();
      this._authSubject.next(this.isAuthenticated);
    }, 3000);
    this._dossierNumber.next(this.getDossierNumber());
  }

  async checkIsAuthenticated(): Promise<boolean> {
    return this.getUserGroups() && (await this.oktaAuth.isAuthenticated());
  }

  login() {
    this.oktaAuth.signInWithRedirect({ originalUri: '/declaration' });
  }

  logout() {
    this.storage.removeItem('account-login');
    this.storage.removeItem('account-first-name');
    this.storage.removeItem('account-last-name');
    this.storage.removeItem('okta-token-storage');
    this.storage.removeItem('expired-access-token-period');
    this.storage.removeItem('refresh-token');
    this.storage.removeItem('user-types');
    this._authSubject.next(false);
  }

  subscribeAuth() {
    return this.oktaAuth.isAuthenticated();
  }

  subscribeUser() {
    return this.oktaAuth.getUser();
  }

  isDossierAuth() {
    return this.getAccountLogin().split('.')[0] === this.getDossierNumber();
  }

  setDossierToken(dossierToken: DossierToken) {
    this._authSubject.next(false);
    const {
      accessToken,
      refreshToken,
      dossierNumber,
      login,
      userTypes,
      idToken,
    } = dossierToken;
    this.setAccessToken(accessToken);
    this.setIdToken(idToken);
    this.setRefreshToken(refreshToken);
    this.setAccountLogin(login);
    this.setUserGroups(userTypes);
    // tslint:disable-next-line:no-unused-expression
    dossierNumber && this.setDossierNumber(dossierNumber);
    this._authSubject.next(true);
  }

  setSuperAdminToken(token) {
    this._authSubject.next(false);
    const { accessToken, refreshToken, login, userTypes, idToken } = token;
    this.setAccessToken(accessToken);
    this.setIdToken(idToken);
    this.setRefreshToken(refreshToken);
    this.setAccountLogin(login);
    this.setUserGroups(userTypes);
    this._authSubject.next(true);
  }

  setPreDeclarationToken(predeclarationToken: PreDeclarationToken) {
    this.setAccessToken(predeclarationToken.accessToken);
    this.setIdToken(predeclarationToken.idToken);
    this.setRefreshToken(predeclarationToken.refreshToken);
    this.setUserGroups(predeclarationToken.userTypes);
    this.setAccountLogin(predeclarationToken.login);
    this._authSubject.next(true);
  }

  setOpenArrivalToken(openArrivalToken: OpenArrivalToken) {
    this.setAccessToken(openArrivalToken.accessToken);
    this.setIdToken(openArrivalToken.idToken);
  }

  setDossierNumber(dossierNumber: string) {
    this.storage.setItem('dossier-number', dossierNumber, sessionStorage);
    this._dossierNumber.next(dossierNumber);
  }

  clearDossierNumber() {
    this.storage.removeItem('dossier-number', sessionStorage);
    this.removeIsDossierAccess();
    this._dossierNumber.next(null);
  }

  setAccessToken(accessToken: AccessToken) {
    this.oktaAuth.tokenManager.add('accessToken', accessToken);
    this.storage.setItem(
      'expired-access-token-period',
      accessToken.expiresAt,
      this.storagePlace
    );
  }

  setRefreshToken(refreshToken: string): void {
    this.storage.setItem('refresh-token', refreshToken, this.storagePlace);
  }

  clearRefreshToken(): void {
    this.storage.removeItem('refresh-token', this.storagePlace);
  }

  setUserGroups(userTypes: string[]) {
    this.storage.setItem('user-types', userTypes, this.storagePlace);
  }

  setRedirectUri(redirectUrl: string): void {
    this.storage.setItem('redirect-uri', redirectUrl);
  }

  setHoldAction(action: CtaAction) {
    this.storage.setItem('action', action, sessionStorage);
  }
  getAndClearHoldAction(): CtaAction {
    const action = this.storage.getItem('action', sessionStorage);
    if (action) this.storage.removeItem('action', sessionStorage);
    return action as CtaAction;
  }

  getRedirectUri(): string {
    return this.storage.getItem('redirect-uri');
  }

  setIsDossierAccess() {
    this.storage.setItem('is-dossier-access', true, sessionStorage);
  }

  getIsDossierAccess(): boolean {
    return this.storage.getItem('is-dossier-access', sessionStorage);
  }

  removeIsDossierAccess(): void {
    this.storage.removeItem('is-dossier-access', sessionStorage);
  }

  setRedirectWcbUri(redirectWcbUrl: string): void {
    this.storage.setItem('redirect-wcb-uri', redirectWcbUrl);
  }

  getRedirectWcbUri(): string {
    return this.storage.getItem('redirect-wcb-uri');
  }

  setRedirectExitUri(redirectExitUrl: string): void {
    this.storage.setItem('redirect-exit-uri', redirectExitUrl);
  }

  getRedirectExitUri(): string {
    return this.storage.getItem('redirect-exit-uri');
  }

  setRedirectNAUri(redirectNAUri: string) {
    this.storage.setItem('redirect-na-uri', redirectNAUri);
  }

  getRedirectNAUri(): string {
    return this.storage.getItem('redirect-na-uri');
  }

  setModifParam(modifParam: string) {
    this.storage.setItem('modif_param', modifParam);
  }

  getModifParam(): string {
    return this.storage.getItem('modif_param');
  }

  removeModifParam() {
    this.storage.removeItem('modif_param');
  }

  getUserGroups() {
    return this.storage.getItem<JsonListKey, string>(
      'user-types',
      this.storagePlace
    );
  }

  userIsManagerWithGroup(
    fitters: Fitter[],
    group: FitterAccessType,
    acceptAdmin = false
  ) {
    return (
      fitters.findIndex(
        (f) =>
          f.managers?.findIndex(
            (m) =>
              m.email === this.getAccountLogin() &&
              m.accessTypes?.includes(group)
          ) > -1
      ) > -1 ||
      (acceptAdmin && this.userIsFitterSuperAdmin())
    );
  }

  userIsFitter() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpFitter');
  }

  userIsFitterSuperAdmin() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpFitterSuperAdmin');
  }

  userIsFitterAdmin() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpFitterAdmin');
  }

  userHasFitterAdminGroupAccess(fitter: Fitter) {
    return (
      this.userIsFitterAdmin() &&
      fitter?.managers?.findIndex(
        (m) =>
          m?.email === this.getAccountLogin() &&
          m?.accessTypes?.includes('grpFitterAdmin')
      ) > -1
    );
  }

  userHasFitterDashboardOktaAccess(acceptAdmin = false) {
    const userGroups = this.getUserGroups();
    return (
      !!userGroups?.includes('grpFitterDashboard') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()))
    );
  }

  userHasFitterDashboardAccess(fitters: Fitter[] = []) {
    if (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()) {
      return true;
    }

    const hasOktaAccess = this.userHasFitterDashboardOktaAccess();

    const hasAtLeastOneFitterAccess = this.userIsManagerWithGroup(
      fitters,
      'grpFitterDashboard',
      true
    );

    return hasOktaAccess && hasAtLeastOneFitterAccess;
  }

  userHasFitterSearchOktaAccess(acceptAdmin = false) {
    const userGroups = this.getUserGroups();
    return (
      !!userGroups?.includes('grpFitterSearch') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()))
    );
  }

  userHasFitterSearchAccess(fitters: Fitter[] = []) {
    if (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()) {
      return true;
    }

    const hasOktaAccess = this.userHasFitterSearchOktaAccess();

    const hasAtLeastOneFitterAccess = this.userIsManagerWithGroup(
      fitters,
      'grpFitterSearch',
      true
    );

    return hasOktaAccess && hasAtLeastOneFitterAccess;
  }

  userHasFitterSettingsOktaAccess(acceptAdmin = false) {
    const userGroups = this.getUserGroups();
    return (
      !!userGroups?.includes('grpFitterParam') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()))
    );
  }

  userHasFitterSettingsAccess(fitters: Fitter[] = []) {
    if (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()) {
      return true;
    }

    const hasOktaAccess = this.userHasFitterSettingsOktaAccess();

    const hasAtLeastOneFitterAccess = this.userIsManagerWithGroup(
      fitters,
      'grpFitterParam',
      true
    );

    return hasOktaAccess && hasAtLeastOneFitterAccess;
  }

  userHasFitterPlanningOktaAccess(configId: string, acceptAdmin = false) {
    const userGroups = this.getUserGroups();
    return (
      !!userGroups?.includes('grpFitter_' + configId) ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()))
    );
  }

  userHasFitterPlanningAccess(configId: string, fitters: Fitter[] = []) {
    if (this.userIsFitterSuperAdmin() || this.userIsFitterAdmin()) {
      return true;
    }

    const hasOktaAccess = this.userHasFitterPlanningOktaAccess(configId);

    const hasAtLeastOneFitterAccess = this.userIsManagerWithGroup(
      fitters,
      `grpFitter_${configId}` as FitterAccessType,
      true
    );

    return hasOktaAccess && hasAtLeastOneFitterAccess;
  }

  userIsInsurerX(insurerCode: string) {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurer_' + insurerCode);
  }

  userIsInsurer() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurer');
  }

  userIsInsurerFromCallCenter() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurerFromCallCenter');
  }

  userIsInsurerAdmin() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurerAdmin');
  }

  userIsBrand() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.some((group) => group?.startsWith('grpBrand'));
  }

  getUserBrandX() {
    const foundGroup = this.getUserGroups()?.find((group) =>
      group.includes('grpBrand_')
    );
    if (foundGroup) {
      return foundGroup.split('_')[1];
    } else {
      return null;
    }
  }

  getFitterB2BBrand() {
    const foundGroup = this.getUserGroups()?.find((group) =>
      group.includes('grpFitterB2B_')
    );
    if (foundGroup) {
      return foundGroup.split('_')[1];
    } else {
      return null;
    }
  }

  isFitterB2B() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.some((group) => group?.startsWith('grpFitterB2B'));
  }

  userIsSuperAdmin() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpSuperAdmin');
  }

  userHasInsurerDashboardAccess() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurerDashboard');
  }

  userHasInsurerPartnerFitterAccess() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurerAccessPartnerFitter');
  }

  userHasSuperAdminAccess() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpSuperAdmin');
  }

  userHasClaimsListAccess() {
    const userGroups = this.getUserGroups();
    return !!userGroups?.includes('grpInsurerAccessClaimsList');
  }

  setIdToken(idToken: any) {
    this.oktaAuth.tokenManager.add('idToken', idToken);
  }

  getAccessToken() {
    return this.oktaAuth.getAccessToken();
  }

  getRefreshToken() {
    return this.storage.getItem('refresh-token', this.storagePlace);
  }

  getExpireAccessTokenDate() {
    return this.storage.getItem(
      'expired-access-token-period',
      this.storagePlace
    );
  }

  getDossierNumber() {
    return this.storage.getItem('dossier-number', sessionStorage);
  }

  setAccountLogin(login: string) {
    this.storage.setItem('account-login', login, this.storagePlace);
  }

  getAccountLogin(): string {
    return this.storage.getItem('account-login', this.storagePlace);
  }

  setAccountFirstName(firstName: string): void {
    this.storage.setItem('account-first-name', firstName, this.storagePlace);
  }

  getAccountFirstName(): string {
    return this.storage.getItem('account-first-name', this.storagePlace);
  }

  setAccountLastName(lastName: string): void {
    this.storage.setItem('account-last-name', lastName, this.storagePlace);
  }

  getAccountLastName(): string {
    return this.storage.getItem('account-last-name', this.storagePlace);
  }

  setEncryptedInfo(encryptedInfo: string): void {
    this.storage.setItem('encrypted-info', encryptedInfo, this.storagePlace);
  }

  getEncryptedInfo(): string {
    return this.storage.getItem('encrypted-info', this.storagePlace);
  }
  notifyAndLogout() {
    this.logout();
    if (!this.isShowErrorDialogOpened) {
      this.isShowErrorDialogOpened = true;
      this.dialogService
        .showError(
          'GlobalErrors.unauthorized',
          'GlobalErrors.unauthorizedDialogBody'
        )
        .subscribe(() => {
          this.isShowErrorDialogOpened = false;
          this.router.navigate([this.authOktaConfig.guardRedirectUrl]);
        });
    }
  }

  logOutAndRedirect() {
    this.logout();
    this.router.navigate([this.authOktaConfig.guardRedirectUrl]);
  }
}
